import React, { useState } from 'react';
import { Save } from 'lucide-react';
import {
  Alert, Badge, Button, Card, Field, Input, Page, PageHead, Select, Stat,
} from '../components/ui';
import { bmiBand, bodyMetrics, n1, shortDate } from '../lib/format';

const ACTIVITY = [
  { value: 'sedentary', label: 'Sedentary — desk job, little exercise' },
  { value: 'light', label: 'Light — 1–3 sessions a week' },
  { value: 'moderate', label: 'Moderate — 3–5 sessions a week' },
  { value: 'active', label: 'Active — 6–7 sessions a week' },
  { value: 'very_active', label: 'Very active — training twice a day' },
];

const GENDERS = [
  { value: '', label: 'Prefer not to say' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];

export default function Profile({ user, stats, weightLog, onSave }) {
  const p = user?.profile || {};
  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    age: p.age ?? '',
    gender: p.gender ?? '',
    height: p.height ?? '',
    current_weight: p.current_weight ?? '',
    target_weight: p.target_weight ?? '',
    activity_level: p.activity_level ?? 'moderate',
    bio: p.bio ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setSaved(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setSaved(false);
    try {
      await onSave({
        full_name: form.full_name || null,
        age: form.age === '' ? null : parseInt(form.age, 10),
        gender: form.gender || null,
        height: form.height === '' ? null : parseFloat(form.height),
        current_weight: form.current_weight === '' ? null : parseFloat(form.current_weight),
        target_weight: form.target_weight === '' ? null : parseFloat(form.target_weight),
        activity_level: form.activity_level || null,
        bio: form.bio || null,
      });
      setSaved(true);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const latest = weightLog[weightLog.length - 1];
  const { bmi, ffmi } = bodyMetrics(
    { height: parseFloat(form.height) || p.height, current_weight: parseFloat(form.current_weight) || p.current_weight },
    latest
  );
  const band = bmiBand(bmi);

  return (
    <Page>
      <PageHead title="Profile">
        These details drive your macro targets, the sex-specific reference ranges used on your
        bloodwork, and the context given to the AI assistant.
      </PageHead>

      <div className="grid grid-4">
        <Stat label="BMI" value={bmi ? n1(bmi) : '–'} note={band.label}
          tone={band.tone === 'muted' ? undefined : band.tone} />
        <Stat label="FFMI" value={ffmi ? n1(ffmi) : '–'}
          note={ffmi ? 'Fat-free mass index' : 'Needs a body-fat reading'} />
        <Stat label="Days active" value={stats?.days_active ?? 0} />
        <Stat label="Member since" value={shortDate(user?.created_at)} />
      </div>

      <div className="grid grid-2">
        <Card title="About you">
          <form onSubmit={submit} className="stack">
            {error && <Alert tone="crit">{error}</Alert>}
            {saved && <Alert tone="ok">Profile saved.</Alert>}

            <Field label="Full name">
              <Input value={form.full_name} onChange={set('full_name')} placeholder="Your name" />
            </Field>

            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <Field label="Age">
                <Input numeric type="number" min="13" max="120" value={form.age}
                  onChange={set('age')} placeholder="25" />
              </Field>
              <Field label="Sex" hint="Used for reference ranges">
                <Select options={GENDERS} value={form.gender} onChange={set('gender')} />
              </Field>
            </div>

            <Field label="Activity level">
              <Select options={ACTIVITY} value={form.activity_level} onChange={set('activity_level')} />
            </Field>

            <Field label="Bio">
              <Input value={form.bio} onChange={set('bio')} placeholder="A line about your goals" />
            </Field>

            <Button variant="primary" icon={Save} type="submit" disabled={busy} loading={busy}>
              Save changes
            </Button>
          </form>
        </Card>

        <div className="stack">
          <Card title="Body">
            <div className="stack">
              <Field label="Height · cm">
                <Input numeric type="number" step="any" value={form.height}
                  onChange={set('height')} placeholder="178" />
              </Field>
              <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <Field label="Current weight · kg">
                  <Input numeric type="number" step="any" value={form.current_weight}
                    onChange={set('current_weight')} placeholder="79" />
                </Field>
                <Field label="Target weight · kg">
                  <Input numeric type="number" step="any" value={form.target_weight}
                    onChange={set('target_weight')} placeholder="72" />
                </Field>
              </div>
              <p className="faint" style={{ fontSize: 12 }}>
                Current weight updates automatically whenever you record a weigh-in.
              </p>
            </div>
          </Card>

          <Card title="Account">
            <div className="stack-tight">
              {[
                ['Username', user?.username],
                ['Email', user?.email],
                ['Account ID', user?.id],
              ].map(([k, v]) => (
                <div className="spread" key={k}>
                  <span className="label">{k}</span>
                  <span style={{ fontSize: 13 }}>{v ?? '–'}</span>
                </div>
              ))}
              <div className="spread" style={{ marginTop: 4 }}>
                <span className="label">Data</span>
                <Badge tone="ok">Stored privately</Badge>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Page>
  );
}
